import type { AccountConfig as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as AccountFeature from './AccountFeature';

/**
 * The signed-in caller's account configuration, read off `currentUser`. Nested under `User/`
 * rather than promoted to its own `App/` directory: it is reachable only through `Me`.
 *
 * Three ways this differs from the legacy `app { accountConfig }` it replaces:
 *
 * - **No `id`.** AppSchema implies the account from the request, so consumers that need the id
 *   take it from the admin account store instead of from here.
 * - **`features` arrives pre-filtered to the enabled ones**, and carries no `enabled` flag to
 *   filter on. The legacy list held both enabled and disabled features.
 * - **`prismAccess` is not selected.** The schema resolves prism access twice over — as that
 *   boolean and as a `prism_integration` entry in the list — and `hasFeature('prism_integration')`
 *   is what the app reads. Carrying the boolean too would only give the two a way to disagree.
 *
 * `name` is kept though nothing reads it today: it is the type's defining field, and this ref is
 * part of the account store's public surface.
 */
export type AccountConfig = {
  name: string;
  features: AccountFeature.AccountFeature[];
};

export const fromGql = (item: GQLItem): AccountConfig => ({
  ...take<Pick<AccountConfig, 'name'>>(item, 'name'),
  features: item.features.map(AccountFeature.fromGql),
});
